import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { Card, CardContent, Chip, makeStyles, Tooltip, Typography } from '@material-ui/core';
import { Autorenew, CheckCircleOutlined } from '@material-ui/icons';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { useHistory } from 'react-router-dom';
import CardBrandIcon from './CardBrandIcon';
import PaymentMethodSummary from './PaymentMethodSummary';

interface PaymentMethodCardProps {
  title?: string;
  subtitle: string;
  ePayType: string;
  accountType: string;
  cardEnding?: string;
  goodThru?: string;
  isAutopay?: boolean;
  isDefault?: boolean;
  paymentMethodId?: number;
  index?: number;
}

const useStyles = makeStyles((theme) => ({
  cardWrap: {
    boxShadow: 'none',
    border: '1px solid #c4c4c4',
    minWidth: 320,
    maxWidth: 640,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover, &:focus-within': {
      backgroundColor: '#E8F4FC',
      borderColor: '#90CAF9',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
    '& svg': {
      height: 48,
    },
  },
  badgeGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  badge: {
    height: 24,
    fontSize: 12,
    fontWeight: 400,
    '& .MuiChip-label': {
      paddingLeft: 4,
      paddingRight: 8,
    },
    '& .MuiChip-icon': {
      marginLeft: 8,
      marginRight: -2,
    },
  },
  autopayBadge: {
    backgroundColor: '#E3F2FD',
    color: theme.palette.primary.main,
    '& .MuiChip-icon': {
      color: theme.palette.primary.main,
    },
  },
  defaultBadge: {
    backgroundColor: '#E8F5E9',
    color: theme.palette.success.dark,
    '& .MuiChip-icon': {
      color: theme.palette.success.dark,
    },
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  title: {
    fontSize: pxToRem(16),
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  secondaryText: {
    fontSize: pxToRem(14),
  },
  error: {
    color: theme.palette.error.main,
  },
  accEnding: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    fontSize: pxToRem(14),
  },
}));

const PaymentMethodCard = ({
  title,
  subtitle,
  ePayType,
  accountType,
  cardEnding,
  goodThru,
  isAutopay,
  isDefault,
  paymentMethodId,
  index = 0,
}: PaymentMethodCardProps): JSX.Element => {
  const classes = useStyles();
  const history = useHistory();

  const handleCardClick = () => {
    if (paymentMethodId) {
      history.push(`/admin/billing/${paymentMethodId}`);
    }
  };

  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const titleRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setIsTitleTruncated(node.scrollWidth > node.clientWidth);
    }
  }, []);

  return (
    <Card className={classes.cardWrap} data-testid={`payment-method-link-card-${index}`} onClick={handleCardClick}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <CardBrandIcon ePayType={ePayType} />
          <div className={classes.badgeGroup}>
            {isAutopay && (
              <Chip
                size="small"
                icon={<Autorenew fontSize="small" />}
                label="Autopay"
                className={clsx(classes.badge, classes.autopayBadge)}
                data-testid="payment-method-autopay-badge"
              />
            )}
            {isDefault && (
              <Chip
                size="small"
                icon={<CheckCircleOutlined fontSize="small" />}
                label="Default"
                className={clsx(classes.badge, classes.defaultBadge)}
                data-testid="payment-method-default-badge"
              />
            )}
          </div>
        </div>
        {title && (
          <Tooltip title={isTitleTruncated ? title : ''} arrow>
            <Typography ref={titleRef} variant="h3" className={classes.title}>
              {title}
            </Typography>
          </Tooltip>
        )}
        {subtitle && (
          <Typography variant="body1" color="textSecondary" className={classes.secondaryText}>
            {subtitle}
          </Typography>
        )}
        <Typography variant="body1" color="textSecondary" className={classes.accEnding}>
          <PaymentMethodSummary
            ePayType={ePayType}
            accountType={accountType}
            cardEnding={cardEnding}
            goodThru={goodThru}
          />
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodCard;
